import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  XMarkIcon, 
  ClockIcon, 
  UserIcon, 
  CalendarIcon,
  BanknotesIcon,
  UsersIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import { DataTable } from '../../common/DataTable';

const RenewalHistoryModal = ({ 
  show, 
  onClose, 
  customer, 
  renewalHistory, 
  loading,
  formatDate 
}) => {
  if (!show) return null;

  const formatCurrency = (value) => {
    if (!value) return 'N/A';
    return `$${parseFloat(value).toLocaleString()}`;
  };

  const formatDateShort = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div className="fixed inset-0 z-[100] overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-75"
          onClick={onClose}
        />

        {/* Center alignment trick */}
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
          &#8203;
        </span>

        {/* Modal panel */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="inline-block w-full max-w-5xl p-4 my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-gray-800 shadow-xl rounded-2xl relative z-10"
        >
            {/* Header */}
            <div className="flex items-center justify-between mb-3 pb-3 border-b border-gray-200 dark:border-gray-700">
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                  Renewal History
                </h3>
                <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-400">
                  {customer?.company || customer?.customername || 'Customer'}
                </p>
              </div>
              <button
                onClick={onClose}
                className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="max-h-[500px] overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
                </div>
              ) : renewalHistory.length === 0 ? (
                <div className="text-center py-8">
                  <ClockIcon className="w-12 h-12 mx-auto text-gray-400 dark:text-gray-600 mb-3" />
                  <p className="text-sm text-gray-600 dark:text-gray-400">No renewal history available</p>
                  <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                    Renewals will appear here once processed
                  </p>
                </div>
              ) : (
                <DataTable
                  columns={[
                    {
                      id: "processed_at",
                      header: "Date & Time",
                      cell: ({ row }) => (
                        <span>{formatDate ? formatDate(row.original.processed_at) : formatDateShort(row.original.processed_at)}</span>
                      ),
                    },
                    {
                      id: "employee_count",
                      header: "Employee Count",
                      cell: ({ row }) =>
                        row.original.changes?.employee_count ? (
                          <div className="flex items-center space-x-1.5">
                            <span className="text-gray-600 dark:text-gray-400">{row.original.changes.employee_count.from || 'N/A'}</span>
                            <ArrowRightIcon className="w-3 h-3 text-gray-400" />
                            <span className="font-semibold text-gray-900 dark:text-white">{row.original.changes.employee_count.to || 'N/A'}</span>
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        ),
                    },
                    {
                      id: "price",
                      header: "Price",
                      cell: ({ row }) =>
                        row.original.changes?.price ? (
                          <div className="flex items-center space-x-1.5">
                            <span className="text-gray-600 dark:text-gray-400">{formatCurrency(row.original.changes.price.from)}</span>
                            <ArrowRightIcon className="w-3 h-3 text-gray-400" />
                            <span className="font-semibold text-green-600 dark:text-green-400">{formatCurrency(row.original.changes.price.to)}</span>
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        ),
                    },
                    {
                      id: "renewal_date",
                      header: "Renewal Date",
                      cell: ({ row }) =>
                        row.original.changes?.renewal_date ? (
                          <div className="flex items-center space-x-1.5">
                            <span className="text-gray-600 dark:text-gray-400">{formatDateShort(row.original.changes.renewal_date.from)}</span>
                            <ArrowRightIcon className="w-3 h-3 text-gray-400" />
                            <span className="font-semibold text-blue-600 dark:text-blue-400">{formatDateShort(row.original.changes.renewal_date.to)}</span>
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        ),
                    },
                    {
                      id: "processed_by",
                      header: "Processed By",
                      cell: ({ row }) => (
                        <div className="flex items-center space-x-1.5">
                          <UserIcon className="w-3 h-3 text-gray-400" />
                          <span>{row.original.processed_by}</span>
                        </div>
                      ),
                    },
                  ]}
                  data={renewalHistory}
                  getRowId={(renewal) => String(renewal.renewal_id)}
                  emptyMessage="No renewal history available"
                  enableColumnVisibility={false}
                  maxBodyHeight="500px"
                />
              )}
            </div>

            {/* Footer */}
            {/* <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={onClose}
                className="w-full px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                Close
              </button>
            </div> */}
        </motion.div>
      </div>
    </div>
  );
};

export default RenewalHistoryModal;
